import { MARKET_SEARCH_PARAMS } from '@bariendo/lib/markets';
import {
  affiliationInForce,
  affiliationRole,
  buildAffiliation,
  PartnerRole,
  partnerRoles,
  planAffiliationChanges,
} from '@bariendo/lib/partner-markets';
import { MultiSelect, Stack } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { Organization, OrganizationAffiliation } from '@medplum/fhirtypes';
import { useMedplum, useSearchResources } from '@medplum/react';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { ExpandableCard } from '@/components/ExpandableCard';
import { alert } from '@/services/alert';

const ROLE_LABEL: Record<PartnerRole, string> = { facility: 'Facility for', anesthesia: 'Anesthesia for' };

const today = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/** The markets a facility or anesthesia partner is paid for (BAR-3042); history comes from the seed. */
export function PartnerMarketsSection({ organization }: { organization: Organization }): ReactElement | null {
  const medplum = useMedplum();
  const roles = partnerRoles(organization);
  const [markets] = useSearchResources('Location', MARKET_SEARCH_PARAMS);
  const [affiliations, setAffiliations] = useState<OrganizationAffiliation[]>([]);

  const load = useCallback(async () => {
    setAffiliations(
      await medplum.searchResources('OrganizationAffiliation', {
        'primary-organization': getReferenceString(organization),
        _count: '200',
      })
    );
  }, [medplum, organization]);

  useEffect(() => {
    if (roles.length) load().catch(() => alert.error("Couldn't load the partner's markets"));
  }, [load, roles.length]);

  if (!roles.length) return null;

  const selectedFor = (role: PartnerRole): string[] =>
    affiliations
      .filter((affiliation) => affiliationRole(affiliation) === role && affiliationInForce(affiliation, today()))
      .flatMap((affiliation) => affiliation.location?.[0]?.reference ?? []);

  const save = async (role: PartnerRole, selected: string[]): Promise<void> => {
    const { open, close } = planAffiliationChanges({ affiliations, role, selected, today: today() });
    try {
      for (const affiliation of close) await medplum.updateResource(affiliation);
      for (const reference of open) {
        const market = (markets ?? []).find((location) => getReferenceString(location) === reference);
        if (market)
          await medplum.createResource(buildAffiliation({ partner: organization, market, role, start: today() }));
      }
    } catch (error) {
      alert.error(`Couldn't save the markets: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
    await load();
  };

  const data = (markets ?? [])
    .flatMap((location) => (location.name ? [{ value: getReferenceString(location), label: location.name }] : []))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <ExpandableCard title="Markets">
      <Stack gap="sm">
        {roles.map((role) => (
          <MultiSelect
            key={role}
            size="xs"
            label={ROLE_LABEL[role]}
            placeholder="Choose markets"
            data={data}
            value={selectedFor(role)}
            onChange={(selected) => void save(role, selected)}
            searchable
          />
        ))}
      </Stack>
    </ExpandableCard>
  );
}
